import {
  DEFAULT_ORGANIZATION_SETTINGS,
  Organization,
  OrganizationSettings,
  UnsavedOrganization,
} from '@paragon/contract-library';
import { designService, userService } from '@paragon/data-connections';
import { enqueueError } from '@paragon/notification-tools';

import { DraftUnsavedOrganization } from '../components/organization/CreateOrganization';
import { organizationSettingsSet, organizationSettingsUpsert } from '../store/organizationSettingsSlice';
import {
  organizationCreate,
  organizationCustomersSet,
  organizationSet,
  organizationUpdate,
} from '../store/organizationsSlice';
import { AsyncThunkActionVoid } from './thunkActions';

export const loadOrganization =
  (name: string): AsyncThunkActionVoid =>
  async (dispatch) => {
    try {
      const organization = await userService.organizations.pGetOrganization(name);
      dispatch(organizationSet([organization]));

      const organizationSettings = await designService.settings.getOrganizationSettings();
      dispatch(
        organizationSettingsSet([
          organizationSettings ?? { ...DEFAULT_ORGANIZATION_SETTINGS, organization: organization.name },
        ]),
      );
    } catch (err) {
      console.error(err);
    }
  };

export const loadOrganizationsAsRoot = (): AsyncThunkActionVoid => async (dispatch) => {
  try {
    const organizations = await userService.organizations.pGetOrganizations();
    dispatch(organizationSet(organizations));

    const organizationSettings = await designService.settings.getAllOrganizationSettings();
    dispatch(organizationSettingsSet(organizationSettings));
  } catch (err) {
    console.error(err);
  }
};

export const createOrganization =
  (newOrganization: DraftUnsavedOrganization): AsyncThunkActionVoid =>
  async (dispatch) => {
    const newOrganizationWithVisibleOrganizations: UnsavedOrganization = {
      ...newOrganization,
      visibleOrganizations: [],
    };

    try {
      const result = await userService.organizations.pCreateOrganization(newOrganizationWithVisibleOrganizations);
      dispatch(organizationCreate(result));
    } catch (error) {
      console.error(error);
      dispatch(enqueueError('Internal server error while attempting to create organization.'));
    }
  };

export const updateOrganization =
  (updatedOrganization: Organization): AsyncThunkActionVoid =>
  async (dispatch) => {
    await userService.organizations.pUpdateOrganization(updatedOrganization);
    dispatch(organizationUpdate(updatedOrganization));
  };

export const addCustomerToOrganization =
  (organization: Organization, customerName: string): AsyncThunkActionVoid =>
  async (dispatch) => {
    const { customers } = await userService.organizations.pAddCustomerToOrganization(organization.name, customerName);
    dispatch(
      organizationCustomersSet({ name: organization.name, customers: customers.map((customer) => customer.name) }),
    );
  };

export const removeCustomerToOrganization =
  (organization: Organization, customerName: string): AsyncThunkActionVoid =>
  async (dispatch) => {
    const { customers } = await userService.organizations.pRemoveCustomerToOrganization(
      organization.name,
      customerName,
    );
    dispatch(
      organizationCustomersSet({ name: organization.name, customers: customers.map((customer) => customer.name) }),
    );
  };

export const upsertOrganizationSettings =
  (settings: OrganizationSettings): AsyncThunkActionVoid =>
  async (dispatch) => {
    dispatch(organizationSettingsUpsert(settings));
    await designService.settings.upsertOrganizationSettings(settings);
  };
